import React, { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import {
  type PainelFinalizadoItem,
  painelService,
} from "../../../services/painelService";
import { BrazilDate } from "../../../utils/BrazilDate";
import { type PainelOrigemFiltro } from "../utils/origemPainel";
import { PAINEL_FINALIZADOS_MODAL_PAGE_SIZE } from "../utils/pedidoSearch";
import { CardPedidoFinalizado } from "./CardPedidoFinalizado";

const FINALIZADOS_MAX_DIAS = 7;

interface PedidosFinalizadosModalProps {
  isOpen: boolean;
  onClose: () => void;
  lojaId?: number;
  origemFiltro?: PainelOrigemFiltro;
  onVerPedidoOriginal?: (orderId: number) => void;
}

function addDaysISO(iso: string, days: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day + days));
  const y = utc.getUTCFullYear();
  const m = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const d = String(utc.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function diasInclusivos(inicio: string, fim: string): number {
  const [y1, m1, d1] = inicio.split("-").map(Number);
  const [y2, m2, d2] = fim.split("-").map(Number);
  const ms = Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1);
  return Math.round(ms / 86_400_000) + 1;
}

export const PedidosFinalizadosModal: React.FC<PedidosFinalizadosModalProps> = ({
  isOpen,
  onClose,
  lojaId,
  origemFiltro = "todos",
  onVerPedidoOriginal,
}) => {
  const hojeInicial = BrazilDate.operationalTodayISO();
  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [dataInicio, setDataInicio] = useState(hojeInicial);
  const [dataFim, setDataFim] = useState(hojeInicial);
  const [page, setPage] = useState(1);
  const [grupos, setGrupos] = useState<PainelFinalizadoItem[][]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const filtersRef = useRef({
    dataInicio: hojeInicial,
    dataFim: hojeInicial,
    busca: "",
    origem: origemFiltro,
    lojaId,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setBuscaDebounced(busca.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    if (!isOpen) {
      const hoje = BrazilDate.operationalTodayISO();
      setBusca((atual) => (atual ? "" : atual));
      setBuscaDebounced((atual) => (atual ? "" : atual));
      setDataInicio((atual) => (atual === hoje ? atual : hoje));
      setDataFim((atual) => (atual === hoje ? atual : hoje));
      setPage((atual) => (atual === 1 ? atual : 1));
      setGrupos((atual) => (atual.length ? [] : atual));
      setTotal((atual) => (atual === 0 ? atual : 0));
      setTotalPages((atual) => (atual === 1 ? atual : 1));
      setErro((atual) => (atual ? null : atual));
      filtersRef.current = {
        dataInicio: hoje,
        dataFim: hoje,
        busca: "",
        origem: origemFiltro,
        lojaId,
      };
      return;
    }

    const filtersChanged =
      filtersRef.current.dataInicio !== dataInicio ||
      filtersRef.current.dataFim !== dataFim ||
      filtersRef.current.busca !== buscaDebounced ||
      filtersRef.current.origem !== origemFiltro ||
      filtersRef.current.lojaId !== lojaId;
    filtersRef.current = {
      dataInicio,
      dataFim,
      busca: buscaDebounced,
      origem: origemFiltro,
      lojaId,
    };
    if (filtersChanged && page !== 1) {
      setPage(1);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErro(null);

    void painelService
      .getFinalizados({
        lojaId,
        page,
        limit: PAINEL_FINALIZADOS_MODAL_PAGE_SIZE,
        dataInicio,
        dataFim,
        q: buscaDebounced,
        canalOrigem: origemFiltro === "todos" ? undefined : origemFiltro,
      })
      .then((res) => {
        if (cancelled) return;
        setGrupos(res.data);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        if (res.page !== page) setPage(res.page);
      })
      .catch((e) => {
        if (cancelled) return;
        setGrupos([]);
        setTotal(0);
        setTotalPages(1);
        setErro(e instanceof Error ? e.message : "Erro ao carregar finalizados");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    lojaId,
    page,
    dataInicio,
    dataFim,
    buscaDebounced,
    origemFiltro,
  ]);

  const hoje = BrazilDate.operationalTodayISO();

  const pagination =
    totalPages > 1 ? (
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={total}
        itemsPerPage={PAINEL_FINALIZADOS_MODAL_PAGE_SIZE}
        onPageChange={setPage}
        className="rounded-md border-0 bg-transparent px-1 py-1"
      />
    ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Finalizados (${total})`}
      panelClassName="max-w-lg"
      compactHeader
      bodyClassName="flex h-[min(82vh,900px)] min-h-0 flex-col gap-2 overflow-hidden bg-neutral-50/80 p-2"
    >
      <div className="flex shrink-0 items-center gap-1.5">
        <div className="flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-2">
          <Search size={14} className="shrink-0 text-neutral-500" aria-hidden />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nº, nome, mesa..."
            className="h-full min-w-0 flex-1 border-0 bg-transparent text-xs text-neutral-800 outline-none placeholder:text-neutral-400"
            aria-label="Buscar pedidos finalizados"
            autoFocus
          />
        </div>
        <input
          type="date"
          value={dataInicio}
          max={hoje}
          aria-label="Data inicial"
          title="De — intervalo máximo de 7 dias"
          onChange={(e) => {
            const value = e.target.value;
            if (!value) return;
            const inicio = value > hoje ? hoje : value;
            let fim = dataFim < inicio ? inicio : dataFim;
            if (diasInclusivos(inicio, fim) > FINALIZADOS_MAX_DIAS) {
              fim = addDaysISO(inicio, FINALIZADOS_MAX_DIAS - 1);
            }
            if (fim > hoje) fim = hoje;
            setDataInicio(inicio);
            setDataFim(fim);
          }}
          className="h-8 w-[7.6rem] shrink-0 rounded-md border border-neutral-300 bg-white px-1.5 text-xs text-neutral-800"
        />
        <input
          type="date"
          value={dataFim}
          max={hoje}
          min={dataInicio}
          aria-label="Data final"
          title="Até — intervalo máximo de 7 dias"
          onChange={(e) => {
            const value = e.target.value;
            if (!value) return;
            const fim = value > hoje ? hoje : value;
            let inicio = dataInicio > fim ? fim : dataInicio;
            if (diasInclusivos(inicio, fim) > FINALIZADOS_MAX_DIAS) {
              inicio = addDaysISO(fim, -(FINALIZADOS_MAX_DIAS - 1));
            }
            setDataInicio(inicio);
            setDataFim(fim);
          }}
          className="h-8 w-[7.6rem] shrink-0 rounded-md border border-neutral-300 bg-white px-1.5 text-xs text-neutral-800"
        />
      </div>

      {erro ? (
        <p className="text-sm text-red-700">{erro}</p>
      ) : loading && grupos.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      ) : grupos.length === 0 ? (
        <p className="text-sm text-neutral-600">
          {buscaDebounced
            ? "Nenhum pedido encontrado para esta busca."
            : "Nenhum pedido finalizado neste período."}
        </p>
      ) : (
        <>
          <div
            className={`min-h-0 flex-1 space-y-1.5 overflow-y-auto ${loading ? "opacity-60" : ""}`}
          >
            {grupos.map((grupo) => {
              const principal = grupo[0];
              if (!principal) return null;
              return (
                <CardPedidoFinalizado
                  key={`${principal.displayId}-${principal.origem}-${principal.orderId}-${principal.finalizadoEm}`}
                  itens={grupo}
                  onVerPedidoOriginal={onVerPedidoOriginal}
                />
              );
            })}
          </div>
          {pagination}
        </>
      )}
    </Modal>
  );
};
